import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';

import UserMenu from '../UserMenu';
import BrandLogo from '../BrandLogo';
import { ContainedWidth } from '../Layout';
import { useMenuItems, PyMenuItem } from '../hooks';
import { isExternalLink, isPathActive, findActiveOwnerLabel } from './utils';

const Spacer = styled.div`
  height: 80px;
`;

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 100;
  width: 100%;
  border-top: 4px solid ${p => p.theme.palette.primary.main};
  background: ${p => p.theme.palette.common.white};
  ${p => p.theme.shadows[1]}
`;

const Layout = styled.div`
  height: 80px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Logo = styled(BrandLogo)`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
`;

const UserMenuWrapper = styled.div`
  width: 100%;
  max-width: 250px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const Menu = styled.nav`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  min-width: 0;
`;

const MenuItem = styled(NavLink).attrs({ className: 'nav-item' })<{ $active?: boolean }>`
  display: block;
  font-size: 16px;
  font-weight: 400;
  padding: 8px 0;
  white-space: nowrap;
  cursor: pointer;
  && {
    color: ${p => p.theme.palette.common.black};
    text-decoration: none;
    border-bottom: 2px solid ${p => p.$active ? p.theme.palette.primary.main : 'transparent'};
  }
  transition: border-color 300ms;
  &.active, &:hover {
    border-color: ${p => p.theme.palette.primary.main}
  }
`;

const FeaturedMenuItem = styled(MenuItem)`
  && {
    background: ${p => p.theme.palette.primary.main};
    color: ${p => p.theme.palette.common.white};
    border-radius: 4px;
    padding: 8px 16px;
    border-bottom: none;
  }
  &.active, &:hover {
    border-color: transparent;
    opacity: 0.9;
  }
`;

const Dropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover ${MenuItem} {
    border-color: ${p => p.theme.palette.primary.main};
  }

  &:hover > div:last-child {
    display: flex;
  }
`;

const DropdownContent = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 220px;
  z-index: 1;
  padding: 8px;
  background-color: ${p => p.theme.palette.common.white};
  box-shadow: ${p => p.theme.shadows[2]};
  border-radius: 4px;
  flex-direction: column;
  gap: 4px;
`;

const DropdownItem = styled(NavLink).attrs({ className: 'nav-item' })`
  display: block;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  && {
    color: ${p => p.theme.palette.common.black};
    text-decoration: none;
  }
  &:hover, &.active {
    background: #f3fbfc;
  }
`;

function NavMenuItem({
  menuItem,
  isChildActive,
  pathname,
}: {
  menuItem: PyMenuItem;
  isChildActive: boolean;
  pathname: string;
}) {
  const { path, label, children, isFeatured } = menuItem;
  const Item = isFeatured ? FeaturedMenuItem : MenuItem;
  if (children) {
    return (
      <Dropdown>
        <MenuItem as="span" $active={isChildActive}>
          {label}
        </MenuItem>
        <DropdownContent>
          {children.map((child) => (
            <DropdownItem
              to={child.path}
              key={child.label}
              isActive={() => isChildActive && isPathActive(pathname, child.path)}
            >
              {child.label}
            </DropdownItem>
          ))}
        </DropdownContent>
      </Dropdown>
    );
  }
  if (isExternalLink(path)) {
    return (
      <Item href={path} as="a" key={label} target="_blank" rel="noopener noreferrer">
        {label}
      </Item>
    );
  }
  return (
    <Item to={path} key={label} exact>
      {label}
    </Item>
  );
}

export const DesktopNavbar: React.FC<{
  adminMode?: boolean;
}> = ({
  adminMode,
}) => {
  const menuItems = useMenuItems();
  const { pathname } = useLocation();
  const activeLabel = findActiveOwnerLabel(pathname, menuItems);

  return (
    <>
      <Spacer />
      <Wrapper>
        <ContainedWidth>
          <Layout>
            <Logo />
            <Menu>
              {menuItems.map((menuItem) => (
                <NavMenuItem
                  menuItem={menuItem}
                  isChildActive={activeLabel === menuItem.label}
                  pathname={pathname}
                  key={menuItem.label}
                />
              ))}
              {adminMode && <MenuItem href="/admin" as="a">Admin</MenuItem>}
            </Menu>
            <UserMenuWrapper>
              <UserMenu />
            </UserMenuWrapper>
          </Layout>
        </ContainedWidth>
      </Wrapper>
    </>
  );
};
